// Product photos: private files in R2, read through signed URLs from the api
// Worker (online only; without a connection a photo's place shows a plain tile).
import { Button, cx, useToast } from '@ims/ui'
import { Camera, ImagePlus, Loader2, Trash2, X } from '@ims/ui/icons'
import { useEffect, useMemo, useRef, useState } from 'react'
import { errorMessage, useOnline } from './hooks.ts'
import { createApi } from './lib/api.ts'
import { preparePhoto } from './lib/images.ts'
import { useSession } from './session.tsx'
import { useWorkspace } from './workspace.tsx'

/** Signed URLs by path, kept for the session until shortly before they expire. */
const signed = new Map<string, { url: string; expiresAt: number }>()
const nowSeconds = () => Math.floor(Date.now() / 1000)
const fresh = (path: string): string | undefined => {
  const hit = signed.get(path)
  return hit && hit.expiresAt - 60 > nowSeconds() ? hit.url : undefined
}

/**
 * URLs for some stored photos. Paths not signed yet are asked for in one
 * request (200 at a time); offline, or on an error, they are simply missing.
 */
export function usePhotoUrls(
  paths: readonly (string | null | undefined)[],
): Record<string, string> {
  const { platform } = useSession()
  const { current } = useWorkspace()
  const online = useOnline()
  const tenantId = current?.tenant_id
  const key = [...new Set(paths.filter((p): p is string => !!p))].sort().join('\n')
  const [version, setVersion] = useState(0)

  useEffect(() => {
    if (!key || !online || !tenantId) return
    const missing = key.split('\n').filter((p) => fresh(p) === undefined)
    if (missing.length === 0) return
    let active = true
    const api = createApi(platform.apiUrl, platform.supabase)
    void (async () => {
      try {
        for (let i = 0; i < missing.length; i += 200) {
          const { urls, expiresAt } = await api.signImages(tenantId, missing.slice(i, i + 200))
          for (const [path, url] of Object.entries(urls)) signed.set(path, { url, expiresAt })
        }
        if (active) setVersion((v) => v + 1)
      } catch {
        // No photo is not worth an error message: the plain tile stays.
      }
    })()
    return () => {
      active = false
    }
  }, [key, online, tenantId, platform])

  return useMemo(() => {
    void version
    const out: Record<string, string> = {}
    for (const path of key ? key.split('\n') : []) {
      const url = fresh(path)
      if (url) out[path] = url
    }
    return out
  }, [key, version])
}

/** A product's photo, or nothing when it has none or it cannot be loaded now. */
export function ProductPhoto({
  url,
  alt,
  className,
}: {
  url: string | undefined
  alt: string
  className?: string
}) {
  const [failed, setFailed] = useState<string | null>(null)
  if (!url || failed === url) return null
  return (
    <img
      src={url}
      alt={alt}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(url)}
      className={cx('object-cover', className)}
    />
  )
}

/** Choosing a picture, making it small and uploading it (online only). */
function usePhotoUpload(onChange: (path: string | null) => void) {
  const { platform } = useSession()
  const { current } = useWorkspace()
  const online = useOnline()
  const toast = useToast()
  const input = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)

  const upload = async (file: File | undefined) => {
    if (!file || !current) return
    setBusy(true)
    try {
      const photo = await preparePhoto(file)
      const api = createApi(platform.apiUrl, platform.supabase)
      onChange((await api.uploadImage(current.tenant_id, photo)).path)
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
      if (input.current) input.current.value = ''
    }
  }

  return { input, busy, online, upload }
}

/**
 * The photo of a product in its form. Choosing a picture uploads it at once
 * (made small first); the path is saved with the product. Needs a connection.
 */
export function PhotoField({
  path,
  onChange,
  name,
}: {
  path: string | null
  onChange: (path: string | null) => void
  /** What the photo is of, for labels. */
  name: string
}) {
  const { input, busy, online, upload } = usePhotoUpload(onChange)
  const url = usePhotoUrls([path])[path ?? '']

  return (
    <div className="flex items-center gap-3 sm:col-span-2">
      <div className="flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-border bg-background text-subtle">
        {path && url ? (
          <ProductPhoto url={url} alt={`Photo of ${name || 'the product'}`} className="size-full" />
        ) : (
          <ImagePlus className="size-6" aria-hidden />
        )}
      </div>
      <div className="min-w-0">
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            loading={busy}
            disabled={!online}
            onClick={() => input.current?.click()}
          >
            {path ? 'Change photo' : 'Add photo'}
          </Button>
          {path && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              icon={<Trash2 className="size-4" />}
              disabled={busy}
              onClick={() => onChange(null)}
            >
              Remove photo
            </Button>
          )}
        </div>
        <p className="mt-1 text-xs text-muted">
          {online
            ? 'A JPEG or PNG. It is made smaller before it is saved.'
            : 'Connect to the internet to add a photo.'}
        </p>
        <input
          ref={input}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          aria-label="Photo file"
          className="sr-only"
          onChange={(e) => void upload(e.target.files?.[0])}
        />
      </div>
    </div>
  )
}

/**
 * A small photo slot (a colour's photo in Sizes & colours): the photo, or a camera to add
 * one. Tapping it chooses a picture; the x beside a photo takes it off.
 */
export function PhotoSlot({
  path,
  onChange,
  name,
  className,
}: {
  path: string | null
  onChange: (path: string | null) => void
  /** What the photo is of, for labels ("Maroon"). */
  name: string
  className?: string
}) {
  const { input, busy, online, upload } = usePhotoUpload(onChange)
  const url = usePhotoUrls([path])[path ?? '']
  return (
    <span className={cx('relative inline-flex shrink-0', className)}>
      <button
        type="button"
        aria-label={path ? `Change the photo of ${name}` : `Add a photo of ${name}`}
        title={online ? undefined : 'Connect to the internet to add a photo.'}
        disabled={busy || !online}
        onClick={() => input.current?.click()}
        className={cx(
          'tap-target flex size-10 items-center justify-center overflow-hidden rounded-lg text-muted disabled:opacity-60',
          path
            ? 'border border-border bg-background'
            : 'border-[1.5px] border-dashed border-border bg-surface',
        )}
      >
        {path && url ? (
          <ProductPhoto url={url} alt={`Photo of ${name}`} className="size-full" />
        ) : busy ? (
          <Loader2 className="size-4 animate-spin" aria-hidden />
        ) : (
          <Camera className="size-4" aria-hidden />
        )}
      </button>
      {path && !busy && (
        <button
          type="button"
          aria-label={`Remove the photo of ${name}`}
          onClick={() => onChange(null)}
          className="absolute -top-1.5 -right-1.5 flex size-5 items-center justify-center rounded-full border border-border bg-surface text-muted hover:text-ink"
        >
          <X className="size-3" aria-hidden />
        </button>
      )}
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        aria-label={`Photo file of ${name}`}
        className="sr-only"
        onChange={(e) => void upload(e.target.files?.[0])}
      />
    </span>
  )
}
